import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = ({ onBackClick }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={onBackClick}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
          <p className="text-gray-600 mt-2">Last updated: December 2024</p>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8 prose prose-gray max-w-none">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using CRM INS PRO ("Service"), you accept and agree to be bound by the terms 
                  and provision of this agreement. These Terms of Service ("Terms") are a legal agreement 
                  between you and Budget Digital Solutions ("Company", "we", "us", or "our").
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700 mb-4">
                  CRM INS PRO is a cloud-based customer relationship management platform designed specifically 
                  for insurance professionals. The Service includes:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Lead and customer management tools</li>
                  <li>Policy and renewal management</li>
                  <li>Commission tracking and reporting</li>
                  <li>Analytics and business intelligence</li>
                  <li>Third-party integrations</li>
                  <li>Multi-tenant and white-label capabilities</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts and Registration</h2>
                <p className="text-gray-700 mb-4">
                  To access the Service, you must:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use Policy</h2>
                <p className="text-gray-700 mb-4">
                  You agree not to use the Service to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Violate any laws or regulations</li>
                  <li>Infringe on intellectual property rights</li>
                  <li>Transmit harmful, offensive, or inappropriate content</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Reverse engineer or attempt to extract source code</li>
                  <li>Use the Service for competitive intelligence gathering</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
                <p className="text-gray-700 mb-4">
                  Subscription fees are charged in advance on a monthly or annual basis. You agree to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Provide current and accurate billing information</li>
                  <li>Pay all charges incurred under your account</li>
                  <li>Notify us of changes to payment information</li>
                  <li>Accept that fees are non-refundable except as required by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Ownership and Protection</h2>
                <p className="text-gray-700 mb-4">
                  You retain ownership of all data you input into the Service. We will:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Maintain industry-standard security measures</li>
                  <li>Not access your data except as necessary to provide the Service</li>
                  <li>Provide data export capabilities</li>
                  <li>Delete your data upon termination as requested</li>
                  <li>Comply with applicable data protection regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Service Availability</h2>
                <p className="text-gray-700 mb-4">
                  We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. 
                  We may perform maintenance that temporarily affects availability.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  The Service and its original content, features, and functionality are owned by 
                  Budget Digital Solutions and are protected by international copyright, trademark, 
                  patent, trade secret, and other intellectual property laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
                <p className="text-gray-700 mb-4">
                  Either party may terminate this agreement:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>With 30 days' written notice</li>
                  <li>Immediately for material breach</li>
                  <li>Immediately for non-payment</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Upon termination, your access will cease and we will delete your data as requested.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, BUDGET DIGITAL SOLUTIONS SHALL NOT BE LIABLE 
                  FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR 
                  USE OF THE SERVICE.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
                <p className="text-gray-700 mb-4">
                  We reserve the right to modify these Terms at any time. We will notify users of 
                  material changes via email or through the Service. Continued use constitutes 
                  acceptance of modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These Terms are governed by the laws of [Your State/Country] without regard to 
                  conflict of law principles. Any disputes will be resolved in the courts of [Your Jurisdiction].
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
                <p className="text-gray-700">
                  For questions about these Terms, please contact us:
                </p>
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Budget Digital Solutions</strong><br />
                    Email: legal@budgetdigitalsolutions.com<br />
                    Phone: (555) 123-4567<br />
                    Address: [Your Business Address]
                  </p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;